/**
 * W_LIVE 모듈 — 다중 라이브 데이터 유틸 (순수 함수).
 * 날짜 그룹화 · 표시 순서 정렬(종료 뒤로) · 진입 포커스 계산.
 */
import dayjs from '@wconcept/wconcept-common/libs/dayjs';
import {
  PromotionWLiveModule,
  CampaignStatus,
} from '@wconcept/wconcept-common/models/renewal/promotion';

/** 포커스·상태 표시에 쓰는 3분류 상태 */
export type WLiveDisplayStatus = 'ONAIR' | 'READY' | 'ENDED';

export interface WLiveDateGroup {
  /** 'YYYY-MM-DD' */
  dateKey: string;
  /** 표시 순서(종료 뒤로 + 시간/진행중우선/moduleId)로 정렬된 목록 */
  lives: PromotionWLiveModule[];
}

export interface WLiveViewModel {
  dateGroups: WLiveDateGroup[];
  focusedDateKey: string | null;
  /** 포커스 그룹 내 포커스 라이브 인덱스 = 스와이퍼 initialSlide */
  focusedSlideIndex: number;
}

export interface WLiveFocusTarget {
  dateKey: string;
  /** 정렬된 그룹 lives 내 인덱스 */
  slideIndex: number;
}

/** 포커스 후보 우선순위 (동일 시간대 tie-break): 진행중 > 예정 > 종료 */
const FOCUS_STATUS_RANK: Record<WLiveDisplayStatus, number> = {
  ONAIR: 0,
  READY: 1,
  ENDED: 2,
};

const DATE_FORMAT = 'YYYY-MM-DD';

export function resolveWLiveDisplayStatus(
  live: PromotionWLiveModule
): WLiveDisplayStatus {
  const campaignStatus: CampaignStatus | undefined =
    live.shopLiveInfo?.campaignStatus;

  if (campaignStatus === 'REHEARSAL') return 'READY';
  if (campaignStatus === 'ONAIR') return 'ONAIR';
  if (campaignStatus === 'READY') return 'READY';
  return 'ENDED';
}

/** 라이브 시간(scheduledStartDate) 취득 — shopLiveInfo 우선, 없으면 모듈 레벨. */
function getWLiveScheduleTime(live: PromotionWLiveModule): string {
  return live.shopLiveInfo?.scheduledStartDate ?? live.scheduledStartDate ?? '';
}

/**
 * 같은 날짜 그룹 내 표시 순서.
 *  1) 종료(ENDED)된 라이브는 뒤로 보낸다.
 *  2) 종료 안 된 라이브끼리: 시간 asc → (동일 시간이면 진행중 우선) → moduleId asc.
 *  3) 종료된 라이브끼리: 시간 asc → moduleId asc.
 *
 * - moduleId는 등록일 필드가 없어 '등록순' 대용(작을수록 먼저 등록).
 * - 종료를 뒤로 보내므로 '종료 안 된 첫 라이브'가 항상 맨 앞(슬라이드 0)에 온다.
 * - 전부 종료면 (1)이 no-op → 결국 시간 asc → moduleId asc (최초 정렬)과 동일.
 */
function sortWLivesForDisplay(
  lives: PromotionWLiveModule[]
): PromotionWLiveModule[] {
  return [...lives].sort((a, b) => {
    const aEnded = resolveWLiveDisplayStatus(a) === 'ENDED' ? 1 : 0;
    const bEnded = resolveWLiveDisplayStatus(b) === 'ENDED' ? 1 : 0;
    if (aEnded !== bEnded) return aEnded - bEnded; // 종료를 뒤로

    const aTime = dayjs(getWLiveScheduleTime(a)).valueOf();
    const bTime = dayjs(getWLiveScheduleTime(b)).valueOf();
    if (aTime !== bTime) return aTime - bTime; // 시간 asc

    // 동일 시간: 진행중(ONAIR) > 예정(READY) > 종료(ENDED)
    const aRank = FOCUS_STATUS_RANK[resolveWLiveDisplayStatus(a)];
    const bRank = FOCUS_STATUS_RANK[resolveWLiveDisplayStatus(b)];
    if (aRank !== bRank) return aRank - bRank;

    return a.moduleId - b.moduleId; // 등록순
  });
}

/**
 * scheduledStartDate(YYYY-MM-DD) 기준 날짜 그룹화.
 * - 날짜 탭 순서 = 날짜 오름차순
 * - 각 그룹 lives = 표시 순서 정렬(종료 뒤로 + 시간/진행중우선/moduleId) 적용
 */
function groupWLivesByDate(
  wLiveData: PromotionWLiveModule[]
): WLiveDateGroup[] {
  const map = new Map<string, PromotionWLiveModule[]>();

  wLiveData.forEach(live => {
    const dateKey = dayjs(live.shopLiveInfo?.scheduledStartDate).format(
      DATE_FORMAT
    );
    const bucket = map.get(dateKey);
    if (bucket) {
      bucket.push(live);
    } else {
      map.set(dateKey, [live]);
    }
  });

  return Array.from(map.keys())
    .sort((a, b) => a.localeCompare(b))
    .map(dateKey => ({
      dateKey,
      lives: sortWLivesForDisplay(map.get(dateKey) ?? []),
    }));
}

/**
 * 데이터 기반(시계 불필요) 포커스 대상 — 초기 렌더/SSR 결정용.
 * (실제 진입 시점의 자정 경계 포커스는 resolveWLiveEntryFocus 참고)
 *
 * lives가 "종료 뒤로 + 시간/진행중우선/moduleId"로 정렬돼 있어,
 * 날짜 asc로 스캔해 종료 안 된 라이브가 있는 첫 그룹의 첫 미종료(=슬라이드 0)를 포커스.
 *  - 종료 안 된 라이브가 아무 그룹에도 없으면(전부 종료) → 마지막 그룹의 마지막 카드.
 */
function resolveWLiveFocusTarget(
  dateGroups: WLiveDateGroup[]
): WLiveFocusTarget | null {
  if (dateGroups.length === 0) return null;

  // 1) 날짜 asc 스캔 → 종료 안 된 라이브가 있는 첫 그룹의 첫 미종료 라이브(정렬상 맨 앞)
  for (let g = 0; g < dateGroups.length; g += 1) {
    const { dateKey, lives } = dateGroups[g];
    const idx = lives.findIndex(
      live => resolveWLiveDisplayStatus(live) !== 'ENDED'
    );
    if (idx !== -1) {
      return { dateKey, slideIndex: idx };
    }
  }

  // 2) 전부 종료 → 마지막 날짜 그룹의 마지막 라이브
  const lastGroup = dateGroups[dateGroups.length - 1];
  const lastIndex = lastGroup.lives.length - 1;
  if (lastIndex < 0) return null;
  return { dateKey: lastGroup.dateKey, slideIndex: lastIndex };
}

/**
 * 진입 시점(now) '자정 경계' 기준 포커스 대상 (날짜 탭 + 슬라이드).
 *
 * 규칙 (원본 로직 복원 + 멀티 라이브 적용):
 *  - 오늘(now의 달력 날짜) 그룹이 있으면 → 그 탭, 슬라이드 0
 *      · 종료 뒤로 정렬돼 있어 첫 미종료가 슬라이드 0. 당일 전부 종료여도 자정까지 오늘 탭 유지.
 *  - 오늘 그룹 없으면 → 다음 방송일(날짜 asc 첫 미래) 그룹, 슬라이드 0
 *  - 오늘도 미래도 없으면(전부 과거) → 마지막 그룹의 마지막 카드
 */
export function resolveWLiveEntryFocus(
  dateGroups: WLiveDateGroup[],
  now: dayjs.Dayjs | Date | string
): WLiveFocusTarget | null {
  if (dateGroups.length === 0) return null;

  const todayKey = dayjs(now).format(DATE_FORMAT);

  const todayGroup = dateGroups.find(g => g.dateKey === todayKey);
  if (todayGroup) return { dateKey: todayGroup.dateKey, slideIndex: 0 };

  const futureGroup = dateGroups.find(g => g.dateKey > todayKey);
  if (futureGroup) return { dateKey: futureGroup.dateKey, slideIndex: 0 };

  const lastGroup = dateGroups[dateGroups.length - 1];
  const lastIndex = lastGroup.lives.length - 1;
  if (lastIndex < 0) return null;
  return { dateKey: lastGroup.dateKey, slideIndex: lastIndex };
}

/**
 * 라이브 데이터 → 뷰모델(날짜 그룹 + 데이터 기반 초기 포커스).
 */
export function buildWLiveViewModel(
  wLiveData: PromotionWLiveModule[]
): WLiveViewModel {
  const dateGroups = groupWLivesByDate(wLiveData);
  const focus = resolveWLiveFocusTarget(dateGroups);

  return {
    dateGroups,
    focusedDateKey: focus?.dateKey ?? null,
    focusedSlideIndex: focus?.slideIndex ?? 0,
  };
}
